import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileScreen, faWifi, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { tapOnPhoneBridge } from '../../../../../services/tap-on-phone/tap-on-phone-bridge';
import classes from './manual-credit-dialog.module.scss';

type TapPhase = 'waiting' | 'error';

type TapOnPhoneDialogProps = {
  isOpen: boolean;
  amountLabel: string;
  onClose: () => void;
  onStart: () => Promise<boolean>;
};

// Summary: Drives the native tap-on-phone terminal and shows tap/approve/decline status while the shared credit spine records the witness.
// Details: docs/function-library/web/contexts-pages.md#tap-on-phone-dialog
export const TapOnPhoneDialog: React.FC<TapOnPhoneDialogProps> = ({ isOpen, amountLabel, onClose, onStart }) => {
  const [phase, setPhase] = useState<TapPhase>('waiting');

  // Latest-callback refs so the charge effect can depend ONLY on isOpen; unstable onStart/onClose (inline parent arrows,
  // paymentlines-keyed callbacks) must never re-run the effect — that double-fires startPayment and cancels the real result.
  const onStartRef = useRef(onStart);
  const onCloseRef = useRef(onClose);
  onStartRef.current = onStart;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setPhase('waiting');
    void onStartRef.current().then((accepted) => {
      if (cancelled) {
        return;
      }
      if (accepted) {
        onCloseRef.current();
      } else {
        setPhase('error');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleCancel = () => {
    tapOnPhoneBridge.cancelPayment();
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleCancel}
      centered
      dir="rtl"
      radius="lg"
      size={440}
      closeButtonProps={{ 'data-testid': 'tap-on-phone-close' } as Record<string, string>}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: '#2f9e6f',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={faMobileScreen as IconProp} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={{ fontWeight: 700 }}>מכשיר</span>
            <span style={{ fontSize: 12, color: '#2f9e6f' }}>הצמידו את הכרטיס לגב המכשיר</span>
          </div>
        </div>
      }
    >
      <div data-testid="tap-on-phone-dialog" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          className={classes.creditCardGradient}
          style={{
            position: 'relative',
            borderRadius: 18,
            padding: 26,
            color: '#fff',
            boxShadow: '0 12px 28px rgba(14, 26, 51, 0.4)',
            minHeight: 184,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
          }}
        >
          <FontAwesomeIcon
            icon={(phase === 'error' ? faTriangleExclamation : faWifi) as IconProp}
            style={{ fontSize: 54, opacity: phase === 'error' ? 0.95 : 0.9, color: phase === 'error' ? '#ffb3b3' : '#fff' }}
          />
          <span data-testid="tap-on-phone-status" style={{ fontSize: 15, fontWeight: 600, textAlign: 'center' }}>
            {phase === 'error' ? 'העסקה נדחתה — נסו שוב' : 'ממתין להצמדת הכרטיס...'}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span style={{ color: '#6b7280' }}>סכום לחיוב</span>
          <span dir="ltr">{amountLabel}</span>
        </div>

        <div className={classes.buttonRow}>
          <button type="button" className={`${classes.btnCommon} ${classes.btnCancel}`} onClick={handleCancel} data-testid="tap-on-phone-cancel">
            סגור
          </button>
        </div>
      </div>
    </Modal>
  );
};
